
  import {mapSectionComponents} from '@/helper/SectionHelper';
  import {getMetaTags} from '@/helper/Seo';
  import LazyHydrate from 'vue-lazy-hydration';
  import {createObjectFromQueryString} from '@/helper/QueryHelper';
  import {scopeHelper} from '@/helper/ScopeHelper';
  import startCase from 'lodash/startCase';
  import axios from 'axios';

  export default {
    name: 'Article',
    components: {
      LazyHydrate,
    },
    async asyncData({route, error, $cachedStrapi, app, redirect, store}) {
      // Query page from Strapi; strapi-change for findOne is required!
      const pagePromise = $cachedStrapi
        .findOne('pages', 'articles-category-article')
        .catch((e) => {
          if (e.statusCode !== 404) {
            throw e;
          }
        });

      // Query Article from Strapi based on slug; strapi-change for findOne is required!
      const articlePromise = $cachedStrapi
        .findOne('articles', route.params.article)
        .catch(async () => {
          // Zweite Abfrage nach Artikel mit Slug OHNE Berücksichtigung vom Published-State
          const slug = route.params.article;
          const readableSlug = startCase(slug.replace(/-/g, ' '));
          const currentQuery = route.query;
          const response = await axios.get(
            process.env.STRAPI_URL + '/articles',
            {
              params: {
                slug,
                _publicationState: 'preview',
              },
            }
          );
          if (response.data.length > 0) {
            // Query parameters for inactive jobs for appended
            const newQueryParam = {
              ...currentQuery,
              mtm_group: 'article-deactivated',
              search: readableSlug,
            };

            const redirectRoute = app.localePath({
              name: 'articles',
              query: newQueryParam,
            });

            // Redirect the user to the main listing page (with status code 302)
            redirect(301, redirectRoute);
          } else {
            // Query parameters for inactive jobs for appended
            const newQueryParam = {
              ...currentQuery,
              mtm_group: 'article-not-found',
              search: readableSlug,
            };

            const redirectRoute = app.localePath({
              name: 'articles',
              query: newQueryParam,
            });

            // Redirect the user to the main listing page (with status code 301)
            redirect(302, redirectRoute);
          }
          return;
        });

      const [page, article] = await Promise.all([pagePromise, articlePromise]);

      if (!scopeHelper(page, process.env.SCOPE_CANDIDATE)) {
        error({statusCode: 404, message: 'Page not found'});
        return;
      }
      if (
        !scopeHelper(article, process.env.SCOPE_CANDIDATE) ||
        !article.article_category
      ) {
        error({statusCode: 404, message: 'Article not found'});
        return;
      }

      let search_url = '';
      if (store.getters['breadcrumbs/hasArticleListingRoute']) {
        const lastArticleListingRoute =
          store.state.breadcrumbs.lastArticleListingRoute;
        const filterValues = createObjectFromQueryString(
          lastArticleListingRoute.substring(
            lastArticleListingRoute.indexOf('?') + 1,
            lastArticleListingRoute.lastIndexOf('#')
          )
        );

        //Add filters only if they exist
        let params = '?';
        if (filterValues.search) {
          params = params + 'search=' + filterValues.search;
        }
        if (article.article_category.slug) {
          params =
            params +
            (filterValues.search ? '&' : '') +
            'category[]=' +
            article.article_category.slug;
        }

        search_url =
          app.localePath({
            name: 'articles-category',
            params: {
              category: article.article_category.slug,
            },
          }) + params;
      }

      const sections = await mapSectionComponents(
        page.sections,
        async function (section) {
          switch (section.component) {
            case 'SectionSmallHero':
              if (article.cover_image.length > 0) {
                section.image = article.cover_image[0];
              }
              break;
            case 'SectionArticleDetails':
              section.article = article;
              break;

            case 'SectionBreadcrumbs':
              section.breadcrumbs = [
                {
                  text: app.i18n.t('page.index'),
                  disabled: false,
                  to: app.localePath({
                    name: 'index',
                  }),
                  nuxt: true,
                  exact: true,
                },
                {
                  text: app.i18n.t('page.articles'),
                  disabled: false,
                  to: app.localePath(
                    store.getters['breadcrumbs/hasArticleListingRoute']
                      ? store.state.breadcrumbs.lastArticleListingRoute.split(
                          '#'
                        )[0]
                      : {
                          name: 'articles',
                        }
                  ),
                  nuxt: true,
                  exact: true,
                },
                {
                  text: article.article_category.title,
                  disabled: false,
                  to: store.getters['breadcrumbs/hasArticleListingRoute']
                    ? search_url.split('#')[0]
                    : app.localePath({
                        name: 'articles-category',
                        params: {
                          category: article.article_category.slug,
                        },
                      }),
                  nuxt: true,
                  exact: true,
                },
                {
                  text: article.title,
                  disabled: true,
                  to: app.localePath({
                    name: 'articles-category-article',
                    params: {
                      category: article.article_category.slug,
                      article: article.slug,
                    },
                  }),
                  nuxt: true,
                  exact: true,
                },
              ];
              break;

            case 'SectionSeoContent':
              if (
                article.seo_content_config &&
                article.seo_content_config.seo_contents &&
                article.seo_content_config.seo_contents[0]
              ) {
                section.seoContent = await $cachedStrapi.findOne(
                  'seo-contents',
                  article.seo_content_config.seo_contents[0].id
                );
              } else if (
                section.seo_content_config &&
                section.seo_content_config.seo_contents &&
                section.seo_content_config.seo_contents[0]
              ) {
                section.seoContent = await $cachedStrapi.findOne(
                  'seo-contents',
                  section.seo_content_config.seo_contents[0].id
                );
              }
              break;
          }
        }
      );

      // Meta-Data mit Werten vom Artikel
      const metaData =
        article && article.meta
          ? article.meta
          : page && page.meta
          ? page.meta
          : {};

      return {
        metaData,
        sections,
      };
    },
    data() {
      return {
        metaData: {},
        sections: [],
      };
    },
    head() {
      return {
        title: this.metaData.title,
        meta: getMetaTags(this.metaData),
      };
    },
  };
